"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { CircleQuestionMark, MessageCircle } from "lucide-react";
const faqItems = [{
  question: "Do I have to make any repairs or clean the house?",
  answer: "No. We buy houses in 'as-is' condition. You don't need to lift a finger, make any repairs, or even clean out the property. We handle everything from junk removal to structural fixes after the sale is complete."
}, {
  question: "Are there any hidden fees or realtor commissions?",
  answer: "Zero. Unlike traditional real estate listings, we don't charge commissions or hidden service fees. We even cover 100% of the closing costs, meaning the cash offer we give you is the exact amount you walk away with."
}, {
  question: "How fast can I actually close and get paid?",
  answer: "We move at your speed. Typically, we can close in as little as 7 to 14 days. If you need more time to move or settle affairs, we can schedule the closing date that works best for your specific timeline."
}, {
  question: "What types of properties do you purchase?",
  answer: "We buy all types of residential properties, including single-family homes, multi-family units, distressed properties, inherited houses in probate, and rentals—even if they have difficult tenants or significant property damage."
}, {
  question: "Is there any obligation once I receive a cash offer?",
  answer: "null at all. Our process is transparent and 100% obligation-free. We provide a fair cash offer based on local market data and property condition, and the decision to move forward is entirely up to you."
}];
export default function CommonInquiries() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="20189" id="seller-concerns-addressed" ref={ref} className={`bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-[0.03] bg-[linear-gradient(to_right,#808080_1px,transparent_1px),linear-gradient(to_bottom,#808080_1px,transparent_1px)] bg-[size:40px_40px]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full pointer-events-none opacity-5">
        <div className="absolute top-20 left-0 w-64 h-64 bg-primary rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-0 w-64 h-64 bg-primary rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-[800px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4 text-foreground uppercase">
              Seller Concerns Addressed
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto font-medium">
              Get clear, transparent answers about our fast cash buying process, 
              fees, and closing timelines.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqItems.map((item, i) => <AccordionItem key={i} value={`item-${i}`} className="border-none" data-index={i}>
                <AccordionTrigger className="
                    px-6 py-6 rounded-sm bg-card border border-border
                    hover:no-underline hover:bg-accent/50 hover:border-primary/50
                    hover:shadow-sm
                    data-[state=open]:bg-card data-[state=open]:border-primary
                    transition-all duration-300 group
                  ">
                  <span className="text-left font-bold text-xl tracking-tight group-hover:text-primary transition-colors font-sans">
                    {item.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4 text-muted-foreground text-base leading-relaxed font-medium">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>

          <div className="mt-16 text-center p-10 rounded-sm bg-card border border-border backdrop-blur-sm">
            <div className="flex flex-col items-center gap-6">
              <div className="p-4 rounded-sm bg-primary/10 text-primary">
                <CircleQuestionMark className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold tracking-tight uppercase">
                Need a Cash Offer Today?
              </h3>
              <p className="text-muted-foreground max-w-md mb-2 font-medium">
                We’re ready to help you move forward without the stress of repairs 
                or realtor fees. Reach out for your free consultation.
              </p>
              <Button asChild size="lg" className="rounded-sm px-10 bg-primary hover:bg-foreground text-primary-foreground font-bold transition-colors uppercase tracking-wider">
                <Link to="#">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Get Your Free Offer
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}